import { useAppBridge } from '@shopify/app-bridge-react';
import { getSessionToken } from '@shopify/app-bridge-utils';
import { useMemo, useState, useEffect } from 'react';
import { config } from '../config';

/**
 * A hook that returns an authenticated fetch function.
 * @returns {Function} An authenticated fetch function.
 */
export const useAuthenticatedFetch = () => {
  const app = useAppBridge();
  const [shopifyAccessToken, setShopifyAccessToken] = useState(null);

  useEffect(() => {
    const getAccessToken = async () => {
      try {
        // Get URL parameters
        const params = new URLSearchParams(window.location.search);
        const shop = params.get('shop');
        const urlToken = params.get('token');
        
        console.log('URL parameters:', { shop, urlToken: urlToken ? urlToken.slice(0, 10) + '...' : null });
        
        if (!shop || !urlToken) {
          console.warn('Missing shop or token in URL');
          return;
        }
        
        console.log('Getting session token...');
        const sessionToken = await getSessionToken(app);
        console.log('Got session token:', sessionToken);
        
        console.log('Verifying token...');
        const response = await fetch(`${config.apiBaseUrl}/verify-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionToken}`,
          },
          body: JSON.stringify({
            token: sessionToken,
            shop,
            accessToken: urlToken
          }),
        });
        
        console.log('Verify token response status:', response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Verify token error:', errorText);
          throw new Error('Failed to verify token');
        }
        
        const data = await response.json();
        console.log('Verify token response:', data);
        
        if (data.valid && data.shopifyAccessToken) {
          console.log('Setting Shopify access token');
          setShopifyAccessToken(data.shopifyAccessToken);
        } else {
          console.warn('No Shopify access token in response');
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    };
    
    getAccessToken();
  }, [app]);

  return useMemo(() => {
    return async (uri, options = {}) => {
      const sessionToken = await getSessionToken(app);
      const shopifyDomain = new URLSearchParams(window.location.search).get('shop');
      
      console.log('Making authenticated request:', {
        uri,
        sessionToken,
        shopifyDomain,
        shopifyAccessToken,
      });
      
      const response = await fetch(uri, {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${sessionToken}`,
          'X-Shopify-Shop-Domain': shopifyDomain,
          'X-Shopify-Access-Token': shopifyAccessToken || '',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Request failed:', {
          status: response.status,
          error: errorText,
        });
      }

      if (response.headers.get('X-Shopify-API-Request-Failure-Reauthorize') === '1') {
        const authUrlHeader = response.headers.get('X-Shopify-API-Request-Failure-Reauthorize-Url');
        const redirect = authUrlHeader ?? `/auth`;
        window.location.assign(redirect);
        return null;
      }

      return response;
    };
  }, [app]);
};
